import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Snackbar,
  Stack,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';

import AddIdeaPopup from '../idea-input/AddIdeaPopup';
import JoinBoardAsParticipant from '../participant/JoinBoardAsParticipant';
import VoteAvailabilityBox from './voting/VoteAvailabilityBox';
import ParticipantsPanel from './panels/ParticipantsPanel';
import ChatPanel from './panels/ChatPanel';
import FeedbackDialog from '../feedback/FeedbackDialog';
import ImportCardsDialog from './dialogs/ImportCardsDialog';
import ShareDialog from './dialogs/ShareDialog';
import ClearBoardDialog from './dialogs/ClearBoardDialog';
import SaveTemplateDialog from './dialogs/SaveTemplateDialog';
import BoardTimerSetupPopover from './BoardTimerSetupPopover';
import BoardOwnerMenu from './BoardOwnerMenu';
import type { Participant } from '../../interfaces/Participant';
import type { BoardChat } from '../../interfaces/BoardChat';
import type { BoardDetails, BoardTimerCompletionActions } from '../../interfaces/BoardDetails';
import type { RankBy } from '../../types/boardCardOrdering';
import type { BoardState } from '../../store/boardStore/types';

const getParticipantNameKey = (name: string) => name.trim().toLowerCase();
const getParticipantsMarker = (participants: Participant[]) => {
  const uniqueSortedNames = Array.from(
    new Set(
      participants
        .map((participant) => getParticipantNameKey(participant.name))
        .filter((nameKey) => nameKey.length > 0)
    )
  ).sort();

  return uniqueSortedNames.join('|');
};

const getLatestChatMarker = (chat: BoardChat[]) => {
  const latestChat = chat[chat.length - 1];

  if (!latestChat) {
    return '';
  }

  return `${latestChat.ownerName}|${latestChat.created}|${latestChat.content}`;
};

type BoardToolbarProps = {
  isLocked: boolean;
  isDotVotingStyle: boolean;
  votingEnabled: boolean;
  hasCurrentUser: boolean;
  availableVotes: number;
  usedVotes: number;
  voteLimit: number;
  chatEnabled: boolean;
  commentsEnabled: boolean;
  canUseRankingControls: boolean;
  hasVoteSorting: boolean;
  boardDetails: BoardDetails;
  participantsEnabled: boolean;
  participantUploadsEnabled: boolean;
  isOwner: boolean;
  canAddCards: boolean;
  randomizeCardPosition: boolean;
  chat: BoardChat[];
  participants: Participant[];
  rankBy: RankBy;
  onRankByChange: (rankBy: RankBy) => void;
  viewParticipantNames: boolean;
  allViewParticipantNames: boolean;
  showVoterNames: boolean;
  privateVotingEnabled: boolean;
  giphyEnabled: boolean;
  onSetParticipantNameVisibility: (mode: 'hidden' | 'owner-only' | 'everyone') => void;
  onToggleShowVoteOwners: () => void;
  onTogglePrivateVoting: () => void;
  onToggleLockBoard: () => void;
  onToggleVoting: () => void;
  onToggleCommenting: () => void;
  onToggleChat: () => void;
  onToggleTimer: () => void;
  onToggleParticipants: () => void;
  onToggleParticipantUploads: () => void;
  onToggleGifs: () => void;
  onExportBoard: () => Promise<void>;
  onSaveBoardTemplate: (input: { name: string; description: string }) => Promise<boolean>;
  onLoadQrCode: (boardId: string) => Promise<string | null>;
  onSendInvitations: BoardState['sendInvitations'];
  onSendFeedback: (comment: string) => Promise<boolean>;
  onClearBoard: (mode: 'votes' | 'all') => Promise<void>;
  onStartTimer: (
    durationSeconds: number,
    completionActions: BoardTimerCompletionActions
  ) => Promise<void>;
  onLogout: () => void;
  onOpenSetBackground: () => void;
  onOpenSettings: () => void;
};

const BoardToolbar = ({
  isLocked,
  isDotVotingStyle,
  votingEnabled,
  hasCurrentUser,
  availableVotes,
  usedVotes,
  voteLimit,
  chatEnabled,
  commentsEnabled,
  canUseRankingControls,
  hasVoteSorting,
  boardDetails,
  participantsEnabled,
  participantUploadsEnabled,
  isOwner,
  canAddCards,
  randomizeCardPosition,
  chat,
  participants,
  rankBy,
  onRankByChange,
  viewParticipantNames,
  allViewParticipantNames,
  showVoterNames,
  privateVotingEnabled,
  giphyEnabled,
  onSetParticipantNameVisibility,
  onToggleShowVoteOwners,
  onTogglePrivateVoting,
  onToggleLockBoard,
  onToggleVoting,
  onToggleCommenting,
  onToggleChat,
  onToggleTimer,
  onToggleParticipants,
  onToggleParticipantUploads,
  onToggleGifs,
  onExportBoard,
  onSaveBoardTemplate,
  onLoadQrCode,
  onSendInvitations,
  onSendFeedback,
  onClearBoard,
  onStartTimer,
  onLogout,
  onOpenSetBackground,
  onOpenSettings,
}: BoardToolbarProps) => {
  const [rankMenuAnchor, setRankMenuAnchor] = useState<HTMLElement | null>(null);
  const [ownerMenuAnchor, setOwnerMenuAnchor] = useState<HTMLElement | null>(null);
  const [timerSetupAnchor, setTimerSetupAnchor] = useState<HTMLElement | null>(null);
  const [chatAnchor, setChatAnchor] = useState<HTMLElement | null>(null);
  const [participantsAnchor, setParticipantsAnchor] = useState<HTMLElement | null>(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isSaveTemplateDialogOpen, setIsSaveTemplateDialogOpen] = useState(false);
  const [isClearBoardDialogOpen, setIsClearBoardDialogOpen] = useState(false);
  const [isClearVotesDialogOpen, setIsClearVotesDialogOpen] = useState(false);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [templateSaveSuccessOpen, setTemplateSaveSuccessOpen] = useState(false);
  const [lastViewedChatMarker, setLastViewedChatMarker] = useState('');
  const [lastViewedParticipantsMarker, setLastViewedParticipantsMarker] = useState('');

  const isRankMenuOpen = Boolean(rankMenuAnchor);
  const isOwnerMenuOpen = Boolean(ownerMenuAnchor);
  const isTimerSetupOpen = Boolean(timerSetupAnchor);
  const isChatOpen = Boolean(chatAnchor);
  const isParticipantsOpen = Boolean(participantsAnchor);
  const canAccessParticipants = participantsEnabled || isOwner;
  const canAccessTimerSetup = isOwner && boardDetails.timerEnabled !== false;
  const latestChatMarker = useMemo(() => getLatestChatMarker(chat), [chat]);
  const hasUnreadChat = latestChatMarker.length > 0 && latestChatMarker !== lastViewedChatMarker;
  const participantsMarker = useMemo(() => getParticipantsMarker(participants), [participants]);
  const hasUnreadParticipants =
    participantsMarker.length > 0 && participantsMarker !== lastViewedParticipantsMarker;
  const uniqueParticipantCount = useMemo(() => {
    const names = new Set(
      participants
        .map((participant) => getParticipantNameKey(participant.name))
        .filter((nameKey) => nameKey.length > 0)
    );

    return names.size;
  }, [participants]);

  useEffect(() => {
    if (isChatOpen) {
      setLastViewedChatMarker(latestChatMarker);
    }
  }, [isChatOpen, latestChatMarker]);

  useEffect(() => {
    if (isParticipantsOpen) {
      setLastViewedParticipantsMarker(participantsMarker);
    }
  }, [isParticipantsOpen, participantsMarker]);

  useEffect(() => {
    if (!chatEnabled) {
      setChatAnchor(null);
    }
  }, [chatEnabled]);

  useEffect(() => {
    if (!canAccessParticipants && participantsAnchor) {
      setParticipantsAnchor(null);
    }
  }, [canAccessParticipants, participantsAnchor]);

  const rankByLabel =
    rankBy === 'board'
      ? null
      : rankBy === 'votes' && hasVoteSorting
      ? 'votes'
      : rankBy === 'comments'
      ? 'comments'
      : 'added';
  const defaultSortLabel = randomizeCardPosition ? 'Sorted randomly' : 'Sorted by board';

  const closeOwnerMenus = () => {
    setOwnerMenuAnchor(null);
  };

  const handleSaveBoardTemplate = async (input: { name: string; description: string }) => {
    const didSave = await onSaveBoardTemplate(input);

    if (didSave) {
      setTemplateSaveSuccessOpen(true);
    }

    return didSave;
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        position: 'fixed',
        top: { xs: 'auto', sm: 16 },
        bottom: { xs: 16, sm: 'auto' },
        left: { xs: 16, sm: 'auto' },
        right: 16,
        height: 52,
        zIndex: 1000,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          borderRadius: 4,
          width: { xs: '100%', sm: 'auto' },
        }}
      >
        <Stack direction="row" sx={{ height: '100%', alignItems: 'center' }}>
          {!isLocked && votingEnabled && isDotVotingStyle && hasCurrentUser && (
            <Stack
              direction="row"
              alignItems="center"
              sx={{ height: '100%', display: { xs: 'none', sm: 'flex' } }}
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={0.75}
                sx={{ pl: 3, pr: isOwner ? 1 : 3 }}
              >
                <VoteAvailabilityBox
                  availableVotes={availableVotes}
                  usedVotes={usedVotes}
                  voteLimit={voteLimit}
                />
                {isOwner && (
                  <Tooltip title="Clear all votes">
                    <IconButton
                      onClick={() => setIsClearVotesDialogOpen(true)}
                      aria-label="Clear all votes"
                      size="small"
                    >
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              <Divider orientation="vertical" flexItem />
            </Stack>
          )}
          {!hasCurrentUser && (
            <Stack direction="row" alignItems="center" sx={{ height: '100%', width: '100%' }}>
              <Box sx={{ width: '100%', px: 1.5 }}>
                <JoinBoardAsParticipant />
              </Box>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: 'none', sm: 'flex' } }}
              />
            </Stack>
          )}
          {(canAccessTimerSetup || chatEnabled || canAccessParticipants) && (
            <Stack direction="row" sx={{ display: { xs: 'none', sm: 'flex' } }}>
              <Stack direction="row" gap={1.5} sx={{ alignItems: 'center', pl: 1, pr: 1.75 }}>
                {canAccessTimerSetup && (
                  <Tooltip title="Board timer">
                    <IconButton
                      onClick={(event) =>
                        setTimerSetupAnchor((currentAnchor) =>
                          currentAnchor ? null : event.currentTarget
                        )
                      }
                      aria-label="Open board timer controls"
                      aria-haspopup="dialog"
                      aria-expanded={isTimerSetupOpen ? 'true' : undefined}
                      sx={{ mt: '-2px' }}
                    >
                      <TimerOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {chatEnabled && (
                  <Tooltip title="Chat">
                    <IconButton
                      onClick={(event) =>
                        setChatAnchor((currentAnchor) =>
                          currentAnchor ? null : event.currentTarget
                        )
                      }
                      aria-label="Open board chat"
                    >
                      <Badge
                        badgeContent={chat.length}
                        color="secondary"
                        sx={{
                          mt: chat.length > 0 ? '4px' : undefined,
                          '& .MuiBadge-badge': {
                            backgroundColor: hasUnreadChat ? '#c13d6c' : '#d3d3d3',
                            color: hasUnreadChat ? '#fff' : '#1d1d1d',
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                          },
                        }}
                      >
                        <ChatBubbleOutlineIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                )}
                {canAccessParticipants && (
                  <Tooltip title="Participants">
                    <IconButton
                      onClick={(event) =>
                        setParticipantsAnchor((currentAnchor) =>
                          currentAnchor ? null : event.currentTarget
                        )
                      }
                      aria-label="Open participants"
                      sx={{ mt: '6px' }}
                    >
                      <Badge
                        badgeContent={uniqueParticipantCount}
                        color="secondary"
                        sx={{
                          '& .MuiBadge-badge': {
                            backgroundColor: hasUnreadParticipants ? '#c13d6c' : '#d3d3d3',
                            color: hasUnreadParticipants ? '#fff' : '#1d1d1d',
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                          },
                        }}
                      >
                        <GroupOutlinedIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              {hasCurrentUser && ((!isLocked && canAddCards) || canUseRankingControls) && (
                <Divider orientation="vertical" flexItem />
              )}
            </Stack>
          )}
          {hasCurrentUser && ((!isLocked && canAddCards) || canUseRankingControls) && (
            <Stack
              direction="row"
              gap={1}
              sx={{
                height: '100%',
                width: { xs: '100%', sm: 'auto' },
                alignItems: 'center',
                px: 1.25,
              }}
            >
              {!isLocked && hasCurrentUser && canAddCards && (
                <AddIdeaPopup canAddCards={canAddCards} />
              )}
              {canUseRankingControls && (
                <Box
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                  }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={(event) => setRankMenuAnchor(event.currentTarget)}
                    aria-haspopup="menu"
                    aria-expanded={isRankMenuOpen ? 'true' : undefined}
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flex: { xs: 1, sm: 'none' },
                    }}
                  >
                    {rankByLabel ? `Sort by ${rankByLabel}` : defaultSortLabel}
                  </Button>
                  <Menu
                    anchorEl={rankMenuAnchor}
                    open={isRankMenuOpen}
                    onClose={() => setRankMenuAnchor(null)}
                  >
                    {hasVoteSorting && (
                      <MenuItem
                        selected={rankBy === 'votes'}
                        onClick={() => {
                          onRankByChange('votes');
                          setRankMenuAnchor(null);
                        }}
                      >
                        Sort by votes
                      </MenuItem>
                    )}
                    {commentsEnabled && (
                      <MenuItem
                        selected={rankBy === 'comments'}
                        onClick={() => {
                          onRankByChange('comments');
                          setRankMenuAnchor(null);
                        }}
                      >
                        Sort by comments
                      </MenuItem>
                    )}
                    <MenuItem
                      selected={rankBy === 'added'}
                      onClick={() => {
                        onRankByChange('added');
                        setRankMenuAnchor(null);
                      }}
                    >
                      Sort by added
                    </MenuItem>
                  </Menu>
                </Box>
              )}
            </Stack>
          )}
          {isOwner && (
            <Stack direction="row" sx={{ display: { xs: 'none', sm: 'flex' } }}>
              <Divider orientation="vertical" flexItem />
              <Tooltip title="Share">
                <IconButton
                  onClick={() => setIsShareDialogOpen(true)}
                  aria-label="Share board"
                  sx={{ mx: 0.75 }}
                >
                  <ShareOutlinedIcon />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem />
              <Tooltip title="Board menu">
                <IconButton
                  onClick={(event) => setOwnerMenuAnchor(event.currentTarget)}
                  aria-label="Open owner menu"
                  aria-haspopup="menu"
                  aria-expanded={isOwnerMenuOpen ? 'true' : undefined}
                  sx={{ mx: 0.75 }}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>
              <BoardOwnerMenu
                anchorEl={ownerMenuAnchor}
                open={isOwnerMenuOpen}
                isLocked={isLocked}
                isDotVotingStyle={isDotVotingStyle}
                votingEnabled={votingEnabled}
                chatEnabled={chatEnabled}
                commentsEnabled={commentsEnabled}
                boardDetails={boardDetails}
                participantsEnabled={participantsEnabled}
                participantUploadsEnabled={participantUploadsEnabled}
                viewParticipantNames={viewParticipantNames}
                allViewParticipantNames={allViewParticipantNames}
                showVoterNames={showVoterNames}
                privateVotingEnabled={privateVotingEnabled}
                giphyEnabled={giphyEnabled}
                onClose={closeOwnerMenus}
                onSetParticipantNameVisibility={onSetParticipantNameVisibility}
                onToggleShowVoteOwners={onToggleShowVoteOwners}
                onTogglePrivateVoting={onTogglePrivateVoting}
                onToggleLockBoard={onToggleLockBoard}
                onToggleVoting={onToggleVoting}
                onToggleCommenting={onToggleCommenting}
                onToggleChat={onToggleChat}
                onToggleTimer={onToggleTimer}
                onToggleParticipants={onToggleParticipants}
                onToggleParticipantUploads={onToggleParticipantUploads}
                onToggleGifs={onToggleGifs}
                onExportBoard={onExportBoard}
                onOpenImportCards={() => setIsImportDialogOpen(true)}
                onOpenClearBoard={() => setIsClearBoardDialogOpen(true)}
                onOpenSaveTemplate={() => setIsSaveTemplateDialogOpen(true)}
                onOpenFeedback={() => setIsFeedbackDialogOpen(true)}
                onLogout={onLogout}
                onOpenSetBackground={onOpenSetBackground}
                onOpenSettings={onOpenSettings}
              />
            </Stack>
          )}
        </Stack>
      </Paper>
      {canAccessParticipants && (
        <Popover
          open={isParticipantsOpen}
          anchorEl={participantsAnchor}
          onClose={() => setParticipantsAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              mt: 1,
              width: { xs: 'calc(100vw - 32px)', sm: 320 },
              maxWidth: '100%',
              borderRadius: 3,
              overflow: 'hidden',
            },
          }}
        >
          <ParticipantsPanel participants={participants} />
        </Popover>
      )}
      {chatEnabled && (
        <Popover
          open={isChatOpen}
          anchorEl={chatAnchor}
          onClose={() => setChatAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              mt: 1,
              width: { xs: 'calc(100vw - 32px)', sm: 420 },
              maxWidth: '100%',
              borderRadius: 3,
              overflow: 'hidden',
            },
          }}
        >
          <ChatPanel />
        </Popover>
      )}
      <BoardTimerSetupPopover
        open={isTimerSetupOpen}
        anchorEl={timerSetupAnchor}
        hasVoting={boardDetails.votingStyle !== 'none'}
        onClose={() => setTimerSetupAnchor(null)}
        onBeforeStart={closeOwnerMenus}
        onStartTimer={onStartTimer}
      />
      <FeedbackDialog
        open={isFeedbackDialogOpen}
        onClose={() => setIsFeedbackDialogOpen(false)}
        onSendFeedback={onSendFeedback}
      />
      <ShareDialog
        open={isShareDialogOpen}
        onClose={() => setIsShareDialogOpen(false)}
        onLoadQrCode={onLoadQrCode}
        onSendInvitations={onSendInvitations}
      />
      <ImportCardsDialog open={isImportDialogOpen} onClose={() => setIsImportDialogOpen(false)} />
      <SaveTemplateDialog
        open={isSaveTemplateDialogOpen}
        onClose={() => setIsSaveTemplateDialogOpen(false)}
        onSaveTemplate={handleSaveBoardTemplate}
      />
      <Snackbar
        open={templateSaveSuccessOpen}
        autoHideDuration={4000}
        onClose={() => setTemplateSaveSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setTemplateSaveSuccessOpen(false)}
          sx={{ width: '100%' }}
        >
          Template saved.
        </Alert>
      </Snackbar>
      <ClearBoardDialog
        open={isClearBoardDialogOpen}
        onClose={() => setIsClearBoardDialogOpen(false)}
        onClearBoard={onClearBoard}
      />
      <ClearBoardDialog
        open={isClearVotesDialogOpen}
        onClose={() => setIsClearVotesDialogOpen(false)}
        onClearBoard={onClearBoard}
        fixedMode="votes"
      />
    </Stack>
  );
};

export default BoardToolbar;
